/**
 * daComponents.jsx — Primitives UI DA réutilisables (middleware DA).
 *
 * §11/§18 — Toute page/feature compose ces primitives ; on ne recrée jamais
 * un styled-component qui duplique ces styles. Si une primitive manque,
 * on l'ajoute ICI puis on l'importe (jamais inline dupliqué).
 *
 * §20 — Tokens importés de ./theme (source unique de vérité).
 * §12 ter — responsive by design : rem, clamp(), media() — zéro px en dur.
 * §12 quinquies — bords arrondis : cards 1rem, boutons 0.625rem.
 * §12 undecies — casse grammaticale par défaut (pas d'uppercase forcé).
 */
import React from 'react';
import styled, { css } from 'styled-components';
import {COLORS, FONTS, RADII, SHADOW, media, FONT_SIZE, FONT_WEIGHT} from './theme';
/* Spinner réexporté depuis ModalDA (DRY — une seule définition) */
export { Spinner } from './ModalDA';

/**
 * MultiSelectDA — Multi-select avec recherche + checkboxes, remplace MUI `<Select multiple>` + custom.
 *
 * §19 — primitive extraite après identification du pattern dans 4 fichiers
 * (TagsDropDown(+Filter) + UtilitiesDropDown(+Filter), batch P4-batch7).
 *
 * UX : bouton-input affiche les noms sélectionnés joints par ", ". Clic ouvre le menu déroulant
 * (max-height 50% viewport, scroll vertical). Menu contient :
 *   - TextField de recherche autofocus en sticky-header
 *   - liste filtrée d'options checkbox + label
 * Click outside ferme. ESC ferme.
 *
 * API :
 *   <MultiSelectDA
 *     options={[{id, name}]}
 *     selectedIds={[1, 2, ...]}
 *     onChange={(ids) => setSelectedIds(ids)}
 *     placeholder="Select..."
 *     $width="12.5rem"
 *   />
 */
const MSRoot = styled.div`
  position: relative;
  display: inline-block;
  vertical-align: middle;
  ${(p) => p.$compact && `display: block; width: 100%;`}
  ${(p) => p.$inline && `flex: 1 1 10rem;`}
`;

const MSButton = styled.button`
  width: ${(p) => p.$width || '12.5rem'};
  min-height: 2.25rem;
  padding: 0.4rem 1.75rem 0.4rem 0.625rem;
  border: 0.0625rem solid ${COLORS.lineStrong};
  border-radius: 0.5rem;
  background: ${COLORS.white} var(--ev-select-arrow) no-repeat right 0.625rem center;
  color: ${COLORS.ink};
  font-family: ${FONTS.body};
  font-size: 0.85rem;
  text-align: left;
  cursor: pointer;
  appearance: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: border-color 0.12s;
  &:focus { outline: none; border-color: ${COLORS.accent}; box-shadow: 0 0 0 0.1875rem ${COLORS.accentSoft}; }
  &:hover { border-color: ${COLORS.accent}; }
  .ms-placeholder { color: ${COLORS.muted}; }

  /* $compact — variant barre de filtre : typo réduite 0.8rem + couleur claire (muted),
     pleine largeur, fond/bordure/radius alignés sur les autres box du FilterBar. */
  ${(p) => p.$compact && `
    width: 100%;
    font-size: 0.8rem;
    color: ${COLORS.muted};
    background-color: ${COLORS.bg};
    border-color: ${COLORS.line};
    border-radius: ${RADII.button};
    min-height: 2.45rem;
  `}

  /* $inline — variant barre de filtre (FilterBar $compact) : MSRoot grandit en flex item,
     le bouton doit remplir 100% de cette largeur (sinon il reste figé à $width → vide à droite). */
  ${(p) => p.$inline && `width: 100%;`}
`;

const MSMenu = styled.div`
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  width: ${(p) => p.$width || '15.625rem'};
  max-height: 50vh;
  overflow-y: auto;
  background: ${COLORS.white};
  border: 0.0625rem solid ${COLORS.line};
  border-radius: ${RADII.card};
  box-shadow: ${SHADOW.hover};
  z-index: 100;
  display: ${(p) => (p.$open ? 'block' : 'none')};
`;

const MSSearchWrap = styled.div`
  position: sticky;
  top: 0;
  background: ${COLORS.white};
  padding: 0.5rem 0.625rem;
  border-bottom: 0.0625rem solid ${COLORS.line};
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const MSSearchIcon = styled.span`
  color: ${COLORS.muted};
  font-size: ${FONT_SIZE.base};
  display: inline-flex;
  flex: 0 0 auto;
`;

const MSSearchInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  font-family: ${FONTS.body};
  font-size: 0.85rem;
  color: ${COLORS.ink};
  background: transparent;
  &::placeholder { color: ${COLORS.muted}; }
`;

const MSItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.625rem;
  cursor: pointer;
  font-family: ${FONTS.body};
  font-size: 0.85rem;
  color: ${COLORS.ink};
  &:hover { background: ${COLORS.bg}; }
`;

const MSCheckbox = styled.input.attrs({ type: 'checkbox' })`
  appearance: none;
  -webkit-appearance: none;
  width: 1rem;
  height: 1rem;
  border: 0.0625rem solid ${COLORS.lineStrong};
  border-radius: 0.25rem;
  background: ${COLORS.white};
  cursor: pointer;
  flex: 0 0 auto;
  position: relative;
  &:checked {
    border-color: ${COLORS.accent};
    background: ${COLORS.accent};
  }
  &:checked::after {
    content: '✓';
    position: absolute;
    top: -0.0625rem;
    left: 0.125rem;
    color: #fff;
    font-size: ${FONT_SIZE.xs};
    font-weight: ${FONT_WEIGHT.heavy};
    line-height: 1;
  }
`;

/** SVG inline search icon (purge MUI icons) */
const SearchSvg = () => (
  <svg width="0.875rem" height="0.875rem" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <line x1="16.5" y1="16.5" x2="21" y2="21" />
  </svg>
);

export function MultiSelectDA({
  options = [],
  selectedIds = [],
  onChange,
  placeholder = 'Select...',
  $width,
  $menuWidth,
  $compact,
  $inline,
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const rootRef = React.useRef(null);

  /* Click outside ferme */
  React.useEffect(() => {
    if (!open) return;
    function handleClickOutside(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    function handleEsc(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  const containsText = (text, q) => text.toLowerCase().indexOf(q.toLowerCase()) > -1;
  const filtered = search ? options.filter((o) => containsText(o.name, search)) : options;

  const labelNames = selectedIds
    .map((id) => options.find((o) => o.id === id)?.name)
    .filter(Boolean)
    .join(', ');

  const toggle = (id) => {
    if (!onChange) return;
    if (selectedIds.includes(id)) onChange(selectedIds.filter((x) => x !== id));
    else onChange([...selectedIds, id]);
  };

  return (
    <MSRoot ref={rootRef} $compact={$compact} $inline={$inline}>
      <MSButton
        type="button"
        $width={$width}
        $compact={$compact}
        $inline={$inline}
        onClick={() => setOpen((x) => !x)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {labelNames || <span className="ms-placeholder">{placeholder}</span>}
      </MSButton>
      <MSMenu $open={open} $width={$menuWidth || $width}>
        <MSSearchWrap>
          <MSSearchIcon><SearchSvg /></MSSearchIcon>
          <MSSearchInput
            autoFocus
            placeholder="Type to search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </MSSearchWrap>
        {filtered.length === 0 ? (
          <div style={{ padding: '0.75rem', color: COLORS.muted, fontSize: '0.85rem', textAlign: 'center' }}>No results</div>
        ) : (
          filtered.map((opt) => (
            <MSItem key={opt.id}>
              <MSCheckbox checked={selectedIds.includes(opt.id)} onChange={() => toggle(opt.id)} />
              <span>{opt.name}</span>
            </MSItem>
          ))
        )}
      </MSMenu>
    </MSRoot>
  );
}

/**
 * PaginationDA — Pagination DA primitive, remplace `@material-ui/lab/Pagination`.
 *
 * §19 — primitive extraite après identification du pattern dans 5 fichiers
 * (ColumnNew/Nft/Tile/TileGroup + Collections/collectionDetail, batch P4-batch5).
 * Sera réutilisée par Authors + Collections/index dans les batches suivants.
 *
 * API compatible MUI Pagination :
 *   <PaginationDA page={page} count={N} onChange={(e, newPage) => setPage(newPage)} />
 *
 * Algorithme d'affichage (variante MUI standard "rounded outlined") :
 *   - count ≤ 7  : toutes les pages affichées
 *   - count > 7  : [1] ... [page-1] [page] [page+1] ... [last]
 *   - flèches [‹] et [›] toujours présentes (disabled au bord)
 */
const PaginationRoot = styled.nav`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
  margin: 1rem 0;
`;

const PaginationButton = styled.button`
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.5rem;
  border: 0.0625rem solid ${(p) => (p.$active ? COLORS.accent : COLORS.lineStrong)};
  background: ${(p) => (p.$active ? COLORS.accent : COLORS.white)};
  color: ${(p) => (p.$active ? '#fff' : COLORS.ink)};
  border-radius: 1rem; /* shape="rounded" MUI default */
  font-family: ${FONTS.body};
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  font-size: 0.85rem;
  cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
  opacity: ${(p) => (p.disabled ? 0.4 : 1)};
  transition: border-color 0.12s, background 0.12s;
  &:hover:not(:disabled) { border-color: ${COLORS.accent}; }
`;

const PaginationEllipsis = styled.span`
  padding: 0 0.25rem;
  color: ${COLORS.muted};
  font-size: 0.85rem;
  user-select: none;
`;

function buildPageList(page, count) {
  if (count <= 7) {
    return Array.from({ length: count }, (_, i) => i + 1);
  }
  /* Toujours afficher 1 et `last`. Ellipses si gap > 1. */
  const pages = new Set([1, count, page, page - 1, page + 1]);
  /* Garder dans [1, count] */
  const sorted = [...pages].filter((p) => p >= 1 && p <= count).sort((a, b) => a - b);
  /* Insérer ellipses ('…') quand gap */
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('…');
    result.push(sorted[i]);
  }
  return result;
}

export function PaginationDA({ page = 1, count = 1, onChange }) {
  if (count <= 1) return null;
  const pages = buildPageList(page, count);
  const fire = (e, p) => { if (onChange && p !== page) onChange(e, p); };

  return (
    <PaginationRoot aria-label="Pagination navigation">
      <PaginationButton
        type="button"
        aria-label="Previous page"
        disabled={page <= 1}
        onClick={(e) => fire(e, page - 1)}
      >‹</PaginationButton>
      {pages.map((p, i) =>
        p === '…' ? (
          <PaginationEllipsis key={`e-${i}`}>…</PaginationEllipsis>
        ) : (
          <PaginationButton
            key={p}
            type="button"
            $active={p === page}
            aria-current={p === page ? 'page' : undefined}
            aria-label={`Go to page ${p}`}
            onClick={(e) => fire(e, p)}
          >{p}</PaginationButton>
        )
      )}
      <PaginationButton
        type="button"
        aria-label="Next page"
        disabled={page >= count}
        onClick={(e) => fire(e, page + 1)}
      >›</PaginationButton>
    </PaginationRoot>
  );
}

/**
 * BackdropDA — Overlay full-screen avec spinner, remplace MUI `<Backdrop>` + `<CircularProgress>`.
 * Pattern de loading global (page-level loader pendant API call long).
 *
 * Usage :
 *   <BackdropDA $open={isLoading} />
 *   <BackdropDA $open={isLoading}>{customContent}</BackdropDA>
 *
 * z-index 9999 par défaut (au-dessus de tout sauf modals). Pour z-index custom : $zIndex prop.
 * §19 — primitive extraite après identification du pattern dans Profile/index (P4 batch1).
 * Sera réutilisée dans batches P4 suivants (Coupon, Authors, MyNFTs, Collections, Transactions).
 */
export const BackdropDA = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(13, 10, 16, 0.55);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: ${(p) => p.$zIndex || 9999};
  color: #fff;
`;

/* ─── Layout ─────────────────────────────────────────────────────────────────── */

/**
 * Fond de page pleine hauteur (DA).
 * Restauré 2026-06-07 — chantier D Axe 7 l'avait retiré à tort : PageShell.jsx l'utilise.
 */
export const PageBg = styled.div`
  background: ${COLORS.bg};
  min-height: 100vh;
  font-family: ${FONTS.body};
  color: ${COLORS.ink};
`;

/**
 * Conteneur centré responsive.
 * $w : largeur max (défaut 82rem) · $p : padding override
 * Offset navbar 'position: absolute' validé 2026-05-31 (preview HTML).
 */
export const Container = styled.div`
  max-width: ${(p) => p.$w || '82rem'};
  margin: 0 auto;
  padding: ${(p) => p.$p || 'clamp(5rem, 10vw, 7rem) clamp(1rem, 4vw, 2rem) 4rem'};

  ${media.md`
    padding-top: clamp(4rem, 8vw, 5.5rem);
  `}
`;

/** Carte DA (border-radius 1rem, ombre douce) */
export const DACard = styled.div`
  background: ${COLORS.white};
  border: 0.0625rem solid ${COLORS.line};
  border-radius: ${RADII.card};
  box-shadow: ${SHADOW.card};
  transition: box-shadow 0.18s;
  &:hover { box-shadow: ${SHADOW.hover}; }
`;

/* ─── Typographie ─────────────────────────────────────────────────────────────── */

/** Titre de page DA (DM Sans 900) — responsive via clamp */
export const PageTitle = styled.h1`
  font-family: ${FONTS.head};
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: ${FONT_WEIGHT.heavy};
  color: ${COLORS.ink};
  margin: 0;
`;

/* ─── Chips / Filtres ────────────────────────────────────────────────────────── */

/** Chip de filtre pill (actif = violet plein, §12 quinquies) */
export const Chip = styled.button`
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.bold};
  font-size: 0.8rem;
  border-radius: ${RADII.pill};
  cursor: pointer;
  padding: 0.45rem 0.9rem;
  border: 0.0625rem solid ${(p) => (p.$active ? COLORS.accent : COLORS.lineStrong)};
  background: ${(p) => (p.$active ? COLORS.accent : COLORS.white)};
  color: ${(p) => (p.$active ? '#fff' : COLORS.ink)};
  transition: all 0.12s;
  white-space: nowrap;
  &:hover { border-color: ${COLORS.accent}; }
`;

/* ─── Boutons ────────────────────────────────────────────────────────────────── */

/** Bouton primaire violet CTA (§12 quinquies : border-radius 0.625rem) */
export const PrimaryButton = styled.button`
  background: ${COLORS.accent};
  color: #fff;
  border: none;
  border-radius: ${RADII.button};
  padding: 0.7rem 1.2rem;
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.extrabold};
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
  &:hover:not(:disabled) { background: ${COLORS.accentHover}; }
  &:disabled { background: ${COLORS.disabledBg}; color: ${COLORS.disabledInk}; cursor: not-allowed; }
`;

/** Bouton vert CTA (actions positives : buy, confirm…) */
export const CtaButton = styled.button`
  background: var(--ev-cta, ${COLORS.cta});
  color: #fff;
  border: none;
  border-radius: ${RADII.button};
  padding: 0.7rem 1.2rem;
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.extrabold};
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
  &:hover:not(:disabled) { background: var(--ev-cta-hover, ${COLORS.ctaHover}); }
  &:disabled { background: ${COLORS.disabledBg}; color: ${COLORS.disabledInk}; cursor: not-allowed; }
`;

/** Bouton rouge danger (actions destructives : supprimer, retrait…) */
export const DangerButton = styled.button`
  background: ${COLORS.danger};
  color: #fff;
  border: none;
  border-radius: ${RADII.button};
  padding: 0.7rem 1.2rem;
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.extrabold};
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
  &:hover:not(:disabled) { background: ${COLORS.dangerDeep}; }
  &:disabled { background: ${COLORS.disabledBg}; color: ${COLORS.disabledInk}; cursor: not-allowed; }
`;

/** Bouton outline / ghost (actions secondaires).
 *  Règle bouton standardisée (dark-safe) : fond transparent, bordure neutre tokenisée
 *  (${COLORS.lineStrong} → blanc translucide en dark = reste visible), texte ${COLORS.ink}
 *  (bascule blanc en dark), hover ${COLORS.subtle} (s'éclaircit en dark). Plus aucun
 *  rgba(13,10,16,..) en dur qui rendait la bordure/le hover invisibles sur fond sombre. */
export const GhostButton = styled.button`
  background: transparent;
  border: 0.0625rem solid ${COLORS.lineStrong};
  border-radius: ${RADII.button};
  padding: 0.6rem 1.1rem;
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.extrabold};
  font-size: 0.85rem;
  color: ${COLORS.ink};
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  white-space: nowrap;
  &:hover { background: ${COLORS.subtle}; border-color: ${COLORS.accent}; }
`;

/* ─── Icône / Avatar ─────────────────────────────────────────────────────────── */

/** Pastille ronde colorée (icône d'action, avatar fallback…) ; $bg, $size */
export const AvatarIcon = styled.div`
  flex: 0 0 auto;
  width: ${(p) => p.$size || '2.75rem'};
  height: ${(p) => p.$size || '2.75rem'};
  border-radius: ${RADII.circle};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${FONT_SIZE.lg};
  color: #fff;
  background: ${(p) => p.$bg || COLORS.accent};
`;

/* ─── Tables ─────────────────────────────────────────────────────────────────── */

/**
 * Primitives de table composables (PAS un DataTable monolithique).
 * Utilisation : <TableShell><TableRow><TableCell>…</TableCell></TableRow></TableShell>
 */
export const TableShell = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${FONTS.body};
  font-size: 0.9rem;
  color: ${COLORS.ink};
`;

export const TableRow = styled.tr`
  border-bottom: 0.0625rem solid ${COLORS.line};
  transition: background 0.1s;
  &:hover { background: ${COLORS.bg}; }
  &:last-child { border-bottom: none; }
`;

export const TableCell = styled.td`
  padding: 0.75rem 1rem;
  vertical-align: middle;
  font-size: ${FONT_SIZE.sm};

  &[data-header] {
    font-family: ${FONTS.head};
    font-weight: ${FONT_WEIGHT.bold};
    font-size: ${FONT_SIZE.xs};
    color: ${COLORS.muted};
    padding-bottom: 0.5rem;
  }
`;

/* ─── Formulaires ────────────────────────────────────────────────────────────── */

/**
 * FormSelect — Select stylé DA (remplace MUI Select).
 * Wraper natif <select> — accessible, léger, sans MUI.
 */
export const FormSelect = styled.select`
  width: 100%;
  padding: 0.625rem 2rem 0.625rem 0.75rem;
  border: 0.0625rem solid ${COLORS.lineStrong};
  border-radius: 0.5rem;
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.base};
  color: var(--ev-ink, ${COLORS.ink});
  background: var(--ev-surface, ${COLORS.white}) var(--ev-select-arrow) no-repeat right 0.75rem center;
  appearance: none;
  cursor: pointer;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: ${COLORS.accent};
    box-shadow: 0 0 0 0.1875rem ${COLORS.accentSoft};
  }
  &:disabled { background-color: ${COLORS.disabledBg}; cursor: not-allowed; color: ${COLORS.muted}; }

  [data-theme="dark"] & { color-scheme: dark; }
`;

/**
 * FormInput — Input texte stylé DA (primitive partagée §11/§74).
 * Prop `$heading` : variante « titre éditable » (h1 italique 900, taille 2rem) utilisée
 * pour l'édition en place du titre de fiche (EditModeShell). Sans le prop = input standard.
 */
export const FormInput = styled.input`
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 0.0625rem solid ${COLORS.lineStrong};
  border-radius: 0.5rem;
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.base};
  color: ${COLORS.ink};
  background: ${COLORS.bgSubtle};
  transition: border-color 0.15s, box-shadow 0.15s;

  &::placeholder { color: ${COLORS.muted}; font-style: italic; }
  &:focus { outline: none; border-color: ${COLORS.accent}; box-shadow: 0 0 0 0.1875rem ${COLORS.accentSoft}; }
  &:disabled { background-color: ${COLORS.disabledBg}; cursor: not-allowed; }

  ${(p) => p.$heading && css`
    font-family: ${FONTS.head};
    font-style: italic;
    font-weight: 900;
    font-size: 2rem;
    letter-spacing: -0.01em;
    line-height: 1.06;
    border-width: 0.09375rem;
    border-color: ${COLORS.line};
    border-radius: 0.7rem;
    padding: 0.55rem 0.8rem;
    &::placeholder { font-weight: 700; }
    ${media.md`font-size: 1.7rem;`}
    ${media.sm`font-size: 1.5rem;`}
  `}
`;

/**
 * FormTextarea — Textarea stylé DA (remplace MUI TextField multiline).
 */
export const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 0.0625rem solid ${COLORS.lineStrong};
  border-radius: 0.5rem;
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.base};
  color: ${COLORS.ink};
  resize: vertical;
  min-height: 6rem;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: ${COLORS.accent};
    box-shadow: 0 0 0 0.1875rem ${COLORS.accentSoft};
  }
  &:disabled { background-color: ${COLORS.disabledBg}; cursor: not-allowed; }
`;

/**
 * MenuDA — Zone de menu déroulant stylée DA.
 * Utilisée pour wrapper les popups de menu (remplacement MUI Menu — P3).
 * $isOpen : gère la visibilité via CSS (pas de portal MUI).
 */
export const MenuDA = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 12rem;
  background: ${COLORS.white};
  border: 0.0625rem solid ${COLORS.line};
  border-radius: ${RADII.card};
  box-shadow: ${SHADOW.hover};
  z-index: 1000;
  padding: 0.375rem 0;
  display: ${(p) => (p.$isOpen ? 'block' : 'none')};
`;

export const MenuItemDA = styled.button`
  display: block;
  width: 100%;
  padding: 0.6rem 1rem;
  text-align: left;
  background: none;
  border: none;
  font-family: ${FONTS.body};
  font-size: 0.9rem;
  color: ${COLORS.ink};
  cursor: pointer;
  transition: background 0.1s;
  &:hover { background: ${COLORS.bg}; }
  &:disabled { color: ${COLORS.muted}; cursor: not-allowed; }
`;

/* ─── Édition inline ─────────────────────────────────────────────────────────── */

/** Crayon d'édition violet plein — overlay sur Banner/Avatar ou inline à côté de texte.
 *  Cf. tasks/edit-profile-da-tracking.md §4.2 + previews profile-edit-mode-overview.html.
 *  Tailles : 'sm' (2rem inline pseudo) / 'md' (2.4rem avatar overlay) / 'lg' (2.6rem banner overlay).
 *  Variant : default = violet plein (icône blanche) — TRÈS visible, marque clairement l'éditabilité.
 *  Usage : <EditPencil $size="md" onClick={...} aria-label="Change picture" />
 *  (icône SVG crayon intégrée — pas besoin de children).
 */
const PencilSvg = () => (
  <svg width="60%" height="60%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);
const EditPencilBtn = styled.button`
  width: ${(p) => (p.$size === 'sm' ? '1.5rem' : p.$size === 'lg' ? '2rem' : '1.75rem')};
  height: ${(p) => (p.$size === 'sm' ? '1.5rem' : p.$size === 'lg' ? '2rem' : '1.75rem')};
  border-radius: 50%;
  background: ${COLORS.accent};
  color: #fff;
  border: 0.14rem solid #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 .25rem .8rem rgba(194, 24, 166, .35);
  padding: 0;
  transition: transform .12s, box-shadow .12s, background .12s;
  flex: 0 0 auto;

  &:hover {
    background: ${COLORS.accentHover};
    box-shadow: 0 .35rem 1rem rgba(194, 24, 166, .5);
    transform: scale(1.08);
  }
  &:active { transform: scale(.95); }
  &:disabled {
    opacity: .5;
    cursor: not-allowed;
    transform: none;
  }
`;
export const EditPencil = (props) => {
  const { children, ...rest } = props;
  return <EditPencilBtn type="button" {...rest}>{children || <PencilSvg />}</EditPencilBtn>;
};

/* ─── NotFoundBlock — entité absente (404) ───────────────────────────────────── */
/**
 * Primitive DA pour rendu uniforme "X not found" sur les pages /author/:id,
 * /3D_Asset/:nftId, /3D_Collectible/:nftId, etc. (§11 + §18 — un seul lieu, composer
 * partout). Wording §10.2 — passé par props pour respect strict du wording validé.
 *
 *   <NotFoundBlock
 *     title="Author not found"
 *     subtitle="The author you are looking for does not exist or has been removed."
 *     onBack={() => history.push('/')}
 *   />
 */
const NotFoundWrap = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: clamp(3rem, 8vw, 6rem) 1.25rem;
  font-family: ${FONTS.body};
  color: ${COLORS.ink};
`;
const NotFoundTitle = styled.h2`
  font-family: ${FONTS.head};
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: ${FONT_WEIGHT.heavy};
  color: ${COLORS.accent};
  margin: 0 0 0.75rem;
`;
const NotFoundSubtitle = styled.p`
  font-size: ${FONT_SIZE.base};
  color: ${COLORS.muted};
  max-width: 32rem;
  margin: 0 0 1.5rem;
  line-height: 1.5;
`;
export const NotFoundBlock = ({ title, subtitle, backLabel = 'Back to Home', onBack }) => (
  <NotFoundWrap role="alert" aria-live="polite">
    <NotFoundTitle>{title}</NotFoundTitle>
    {subtitle && <NotFoundSubtitle>{subtitle}</NotFoundSubtitle>}
    {onBack && (
      <PrimaryButton type="button" onClick={onBack}>{backLabel}</PrimaryButton>
    )}
  </NotFoundWrap>
);

/* ─── RatingDA — étoiles SVG inline (remplace @material-ui/lab/Rating) ────────
 * §19 — primitive extraite après identification du pattern dans VariableItem +
 * VariableItemAssetDetail (P4 final cleanup batch A, 2026-06-06).
 * SVG inline (pas d'icon font), accent violet pour les étoiles remplies,
 * gris clair pour les vides. Supporte readOnly + interactive + max custom.
 *
 * API compatible MUI Rating (subset utilisé) :
 *   <RatingDA value={3} max={5} readOnly />
 *   <RatingDA value={value} max={10} onChange={(e, newValue) => setValue(newValue)} />
 */
const RatingRoot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.125rem;
  line-height: 1;
`;
const RatingStarBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: ${(p) => (p.$readOnly ? 'default' : 'pointer')};
  display: inline-flex;
  color: ${(p) => (p.$filled ? COLORS.accent : COLORS.lineStrong)};
  transition: color 0.12s, transform 0.12s;
  &:hover {
    ${(p) => !p.$readOnly && `transform: scale(1.15);`}
  }
  &:focus { outline: none; }
`;
const StarSvg = () => (
  <svg width="1.1rem" height="1.1rem" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);
export function RatingDA({ value = 0, max = 5, readOnly = false, onChange, name }) {
  const v = Math.max(0, Math.min(max, Number(value) || 0));
  const handleClick = (e, newValue) => {
    if (readOnly || !onChange) return;
    onChange(e, newValue);
  };
  return (
    <RatingRoot role="img" aria-label={`Rating ${v} out of ${max}`}>
      {Array.from({ length: max }, (_, i) => i + 1).map((i) => (
        <RatingStarBtn
          key={i}
          type="button"
          $filled={i <= v}
          $readOnly={readOnly}
          tabIndex={readOnly ? -1 : 0}
          aria-label={readOnly ? undefined : `${i} of ${max}`}
          onClick={(e) => handleClick(e, i)}
          name={name}
        >
          <StarSvg />
        </RatingStarBtn>
      ))}
    </RatingRoot>
  );
}

/* ─── SliderDA — input range stylé DA (remplace @material-ui/core/Slider) ────
 * §19 — primitive extraite après identification du pattern dans Maps/tileMap.jsx
 * (P4 final cleanup batch A, 2026-06-06). Support orientation horizontale (défaut)
 * et verticale (zoom slider du tile map).
 *
 * API (subset compatible MUI Slider) :
 *   <SliderDA min={0} max={100} value={v} onChange={(e, val) => setV(val)} />
 *   <SliderDA orientation="vertical" value={zoomScroll} readOnly />
 */
const SliderHRoot = styled.input.attrs({ type: 'range' })`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 0.5rem;
  background: ${COLORS.lineStrong};
  border-radius: 0.25rem;
  outline: none;
  cursor: pointer;
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 1.25rem;
    height: 1.25rem;
    border-radius: ${RADII.circle};
    background: ${COLORS.accent};
    border: 0.125rem solid #fff;
    box-shadow: 0 0.125rem 0.375rem rgba(0,0,0,0.15);
    cursor: pointer;
  }
  &::-moz-range-thumb {
    width: 1.25rem;
    height: 1.25rem;
    border-radius: ${RADII.circle};
    background: ${COLORS.accent};
    border: 0.125rem solid #fff;
    cursor: pointer;
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
/* Vertical : on transforme l'input horizontal via rotation. Largeur = hauteur logique. */
const SliderVWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: ${(p) => p.$length || '8rem'};
  position: relative;
`;
const SliderVInput = styled(SliderHRoot)`
  width: ${(p) => p.$length || '8rem'};
  transform: rotate(-90deg);
  transform-origin: center;
  position: absolute;
`;
export function SliderDA({
  min = 0,
  max = 100,
  step = 1,
  value,
  defaultValue,
  onChange,
  orientation = 'horizontal',
  disabled = false,
  $length,
  'aria-label': ariaLabel,
}) {
  const handleChange = (e) => {
    if (onChange) onChange(e, Number(e.target.value));
  };
  if (orientation === 'vertical') {
    return (
      <SliderVWrap $length={$length}>
        <SliderVInput
          $length={$length}
          min={min}
          max={max}
          step={step}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-orientation="vertical"
        />
      </SliderVWrap>
    );
  }
  return (
    <SliderHRoot
      min={min}
      max={max}
      step={step}
      value={value}
      defaultValue={defaultValue}
      onChange={handleChange}
      disabled={disabled}
      aria-label={ariaLabel}
    />
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
 * PRIMITIVES — STYLE ÉPURÉ (Prop 2 / DA bid-offer)
 *
 * Règle DA §62 : style minimal, sans boîtes colorées sur les stats.
 * Codifie le langage visuel validé 2026-06-13 (Proposition 2 bid-offer-system).
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Règles d'or :
 *   1. Roboto Mono UNIQUEMENT pour les montants/codes/compteurs en standalone — jamais
 *      mélangé avec DM Sans dans la même zone compacte (label + valeur).
 *   2. Couleur accent sur UNE SEULE valeur clé par panneau (prix top/enchère plus haute).
 *      Toutes les autres valeurs en COLORS.ink.
 *   3. Stats : séparateur border-left uniquement — aucun fond coloré (accentSoft/goldSoft).
 *   4. Notes / avertissements : texte plat (EscrowNote) — aucune box à fond coloré.
 *   5. Statut : dot + texte inline — aucun chip à fond (sauf StatusChip pour compat legacy).
 */

/* ─── StatusDot — indicateur dot + label inline ──────────────────────────────
 * Usage : <StatusDot $color="accent">Active auction</StatusDot>
 * $color : 'accent' (défaut) | 'muted' | 'cta' | 'danger'
 */
const StatusDotWrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-family: ${FONTS.body};
  font-size: 0.85rem;
  color: ${COLORS.ink};
`;
const StatusDotCircle = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: ${RADII.circle};
  flex-shrink: 0;
  background: ${(p) => ({
    accent: COLORS.accent,
    cta: COLORS.cta,
    danger: COLORS.danger,
    muted: COLORS.muted,
  }[p.$color] || COLORS.accent)};
`;
export const StatusDot = ({ $color = 'accent', children }) => (
  <StatusDotWrap>
    <StatusDotCircle $color={$color} />
    {children}
  </StatusDotWrap>
);

/* ─── BidValueGrid + BidValueCol — stats 2 colonnes sans fond coloré ─────────
 * Usage :
 *   <BidValueGrid>
 *     <BidValueCol $highlight label="Highest bid" sub="by Alex">$245</BidValueCol>
 *     <BidValueCol label="Min. to bid" sub="$25 step">$270</BidValueCol>
 *   </BidValueGrid>
 *
 * Règle §62-3 : aucun fond — séparateur border-left uniquement entre colonnes.
 * La colonne $highlight affiche la valeur en COLORS.accent, les autres en COLORS.ink.
 */
export const BidValueGrid = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$cols ? `repeat(${p.$cols}, 1fr)` : 'repeat(2, 1fr)'};
  margin-bottom: ${(p) => p.$mb || '1.2rem'};
`;

const BidValueColRoot = styled.div`
  padding: 0.45rem 0.5rem 0.45rem 0;
  & + & {
    padding-left: 1.1rem;
    border-left: 0.0625rem solid ${COLORS.line};
  }
`;
const BidValueLabel = styled.div`
  font-family: ${FONTS.body};
  font-size: 0.77rem;
  color: ${COLORS.muted};
  margin-bottom: 0.2rem;
`;
const BidValueAmount = styled.div`
  font-family: ${FONTS.mono};
  font-weight: ${FONT_WEIGHT.bold};
  font-size: ${(p) => p.$size || '1.5rem'};
  color: ${(p) => (p.$highlight ? COLORS.accent : COLORS.ink)};
  letter-spacing: -0.02em;
  line-height: 1;
`;
const BidValueSub = styled.div`
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.xs};
  color: ${COLORS.muted};
  margin-top: 0.2rem;
`;
export function BidValueCol({ $highlight, $size, label, sub, children }) {
  return (
    <BidValueColRoot>
      {label && <BidValueLabel>{label}</BidValueLabel>}
      <BidValueAmount $highlight={$highlight} $size={$size}>{children}</BidValueAmount>
      {sub && <BidValueSub>{sub}</BidValueSub>}
    </BidValueColRoot>
  );
}

/* ─── InlineCountdown — compteur inline monospace ────────────────────────────
 * Usage : <InlineCountdown>2d 14h 32m</InlineCountdown>
 * Règle §62-1 : texte mono compact sur une ligne — pas de blocs séparés par unité.
 */
export const InlineCountdown = styled.span`
  font-family: ${FONTS.mono};
  font-weight: ${FONT_WEIGHT.semibold};
  font-size: ${(p) => p.$size || '0.85rem'};
  color: ${(p) => (p.$urgent ? COLORS.danger : COLORS.ink)};
`;

/* ─── EscrowNote — notice texte plat (hold/escrow info) ─────────────────────
 * Usage : <EscrowNote><strong>$270.00</strong> will be held…</EscrowNote>
 * Règle §62-4 : texte plat — aucune box à fond coloré.
 */
export const EscrowNote = styled.p`
  font-family: ${FONTS.body};
  font-size: 0.8rem;
  color: ${COLORS.muted};
  line-height: 1.5;
  margin: 0 0 ${(p) => p.$mb || '1.2rem'};
  strong { color: ${COLORS.ink}; font-weight: ${FONT_WEIGHT.medium}; }
`;

/* ─── OfferCardShell — carte offre avec liseré gauche coloré ─────────────────
 * Usage :
 *   <OfferCardShell $variant="new">…</OfferCardShell>
 *   <OfferCardShell $variant="counter">…</OfferCardShell>
 *   <OfferCardShell $variant="accepted">…</OfferCardShell>
 *   <OfferCardShell $variant="pending">…</OfferCardShell>   (défaut)
 *
 * Règle §62 : identité visuelle via border-left uniquement — pas de fond de header coloré.
 * Variants : new (accent) · counter (gold) · accepted (cta green) · pending (line)
 */
const OFFER_BORDER = {
  new: COLORS.accent,
  counter: COLORS.gold,
  accepted: COLORS.cta,
  pending: COLORS.line,
};
export const OfferCardShell = styled.div`
  background: ${COLORS.white};
  border-radius: ${RADII.card};
  border: 0.0625rem solid ${COLORS.line};
  border-left: 0.25rem solid ${(p) => OFFER_BORDER[p.$variant] || COLORS.line};
  box-shadow: 0 0.125rem 0.625rem rgba(60, 10, 70, 0.05);
  overflow: hidden;
  margin-bottom: ${(p) => p.$mb || '0.75rem'};
  ${media.md`border-radius: ${RADII.button};`}
`;
